import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import { useServices } from "../lib/servicesCache";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/useSEO";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/72a579c8-478b-4bc4-a64a-4bd179574544/images/64a054ba993e19d758cf775bb814c783a1f50eca34f1a203d2af90f7832ec15e.png";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const titleKey =
    slug === "wheelie-bin-cleaning"
      ? "seo.service_bin_title"
      : "seo.service_driveway_title";
  const descKey =
    slug === "wheelie-bin-cleaning"
      ? "seo.service_bin_desc"
      : "seo.service_driveway_desc";
  useSEO({ titleKey, descriptionKey: descKey });
  const services = useServices();
  const navigate = useNavigate();

  // Snap to the very top whenever the slug changes (clicking an "other" service
  // card from the cross-sell). ScrollToTop already handles initial route entry.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  // First-ever cold load — services list is null → render a skeleton that
  // matches the final layout so there's no jump when real data arrives.
  if (!services) return <ServiceDetailSkeleton />;

  const svc = services.find((s) => s.slug === slug);
  if (!svc) {
    return (
      <div className="max-w-7xl mx-auto px-5 py-24 text-center">
        <h2 className="font-display text-3xl font-bold text-slate-900">
          {t("nav.services")}
        </h2>
        <p className="mt-3 text-slate-500">404</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 text-sky-700 font-semibold"
        >
          <ArrowLeft className="w-4 h-4" /> {t("nav.home")}
        </Link>
      </div>
    );
  }

  const others = services.filter((s) => s.slug !== svc.slug);

  return (
    <div data-testid="service-detail-page">
      <section className="bg-water-grad py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-700"
            >
              <ArrowLeft className="w-4 h-4" /> {t("nav.home")}
            </Link>
            <h1 className="font-display mt-5 text-5xl font-bold tracking-tight text-slate-900">
              {lang === "pl" ? svc.name_pl : svc.name_en}
            </h1>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">
              {lang === "pl" ? svc.description_pl : svc.description_en}
            </p>
            <div className="mt-7 flex items-baseline gap-3">
              <span className="font-display text-5xl font-bold text-sky-700">
                {svc.base_price.toFixed(0)}
              </span>
              <span className="text-slate-500">
                {t("common.currency")} / {lang === "pl" ? svc.unit_pl : svc.unit_en}
              </span>
            </div>
            <button
              onClick={() => navigate(`/book?service=${svc.slug}`)}
              className="mt-7 btn-primary inline-flex items-center gap-2"
              data-testid="service-detail-book-btn"
            >
              {t("nav.book")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden shadow-xl border border-white">
              <img src={HERO} alt={svc.slug} className="w-full h-80 object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5">
          <h2 className="font-display text-3xl font-bold tracking-tight">
            {t("service_detail.included")}
          </h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            {(lang === "pl" ? svc.features_pl : svc.features_en).map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
              >
                <CheckCircle2 className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 text-sm">{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 rounded-2xl bg-emerald-50 border border-emerald-200 p-5 flex gap-4">
            <ShieldCheck className="w-6 h-6 text-emerald-700 flex-shrink-0" />
            <div className="text-sm text-emerald-900">
              {t("service_detail.payment_note")}
            </div>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="py-16 bg-slate-50/60 border-t border-slate-200">
          <div className="max-w-5xl mx-auto px-5">
            <div className="text-center mb-10">
              <div className="text-sm font-semibold tracking-widest text-sky-600 uppercase">
                {t("service_detail.also_available_eyebrow")}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
                {t("service_detail.also_available_title")}
              </h2>
              <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
                {t("service_detail.also_available_subtitle")}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {others.map((o) => (
                <Link
                  key={o.id}
                  to={`/services/${o.slug}`}
                  className="group rounded-2xl bg-white border border-slate-200 hover:border-sky-300 p-6 shadow-sm hover:shadow-md transition-all flex flex-col"
                  data-testid={`other-service-card-${o.slug}`}
                >
                  <div className="font-display text-xl font-bold text-slate-900 group-hover:text-sky-700 transition-colors">
                    {lang === "pl" ? o.name_pl : o.name_en}
                  </div>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-3 flex-1">
                    {lang === "pl" ? o.description_pl : o.description_en}
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <span className="font-display text-2xl font-bold text-sky-700">
                        {o.base_price.toFixed(0)}
                      </span>
                      <span className="text-xs text-slate-500 ml-1">
                        {t("common.currency")} /{" "}
                        {lang === "pl" ? o.unit_pl : o.unit_en}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700 group-hover:gap-2 transition-all">
                      {t("services.learn")} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function ServiceDetailSkeleton() {
  return (
    <div data-testid="service-detail-skeleton" aria-hidden="true">
      <section className="bg-water-grad py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 space-y-5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-12 w-1/2" />
            <div className="space-y-2 pt-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-9/12" />
            </div>
            <Skeleton className="h-14 w-32 mt-5" />
            <Skeleton className="h-11 w-40 rounded-full mt-2" />
          </div>
          <div className="lg:col-span-5">
            <Skeleton className="rounded-3xl w-full h-80" />
          </div>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5">
          <Skeleton className="h-8 w-48" />
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
